import { getCities } from '../../../api/authApi'
import { getBusinessDistrictsByCityId } from '../../../api/businessDistrictsApi'
import { createCorporateLocation, updateCorporateLocation } from '../../../api/corporateApi'
import translate from '../../../i18n'
import {
  ICorporateLocation,
  ICorporateLocationRequest,
} from '../../../pages/corporates-container/share-type'
import { IBusinessDistrict } from '../../../share-types'
import { ReduxStore } from '../../../store'
import type { City } from '../../../store/city/reducer'
import { Button, Form, Input, InputNumber, Modal, notification, Select, Space } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

interface CorporateLocationModalProps {
  open: boolean
  corporateId: number
  location?: ICorporateLocation
  onCancel: () => void
  onSaved: () => void
}

const CorporateLocationModal = ({
  open,
  corporateId,
  location,
  onCancel,
  onSaved,
}: CorporateLocationModalProps) => {
  const [form] = Form.useForm<ICorporateLocationRequest>()
  const locale = useSelector((state: ReduxStore) => state.global.locale)
  const cityId = Form.useWatch('cityId', form)
  const [cities, setCities] = useState<City[]>([])
  const [businessDistricts, setBusinessDistricts] = useState<IBusinessDistrict[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    getCities().then(setCities)
    form.setFieldsValue(
      location
        ? {
            cityId: location.cityId,
            businessDistrictId: location.businessDistrictId,
            address: location.address,
            longitude: location.longitude,
            latitude: location.latitude,
            memberCount: location.memberCount,
          }
        : {
            cityId: undefined,
            businessDistrictId: undefined,
            address: '',
            longitude: undefined,
            latitude: undefined,
            memberCount: undefined,
          },
    )
  }, [form, location, open])

  useEffect(() => {
    if (!open || !cityId) {
      setBusinessDistricts([])
      return
    }
    getBusinessDistrictsByCityId(cityId).then(res =>
      setBusinessDistricts(res.filter(item => item.isActive)),
    )
  }, [cityId, open])

  const cityOptions = useMemo(
    () =>
      cities.map(city => ({
        label: city.translations.find(item => item.locale === locale)?.name || String(city.id),
        value: city.id,
      })),
    [cities, locale],
  )

  const businessDistrictOptions = useMemo(
    () =>
      businessDistricts.map(item => ({
        label:
          item.translations.find(translation => translation.locale === locale)?.name || item.id,
        value: item.id,
      })),
    [businessDistricts, locale],
  )

  const handleFinish = (values: ICorporateLocationRequest) => {
    setLoading(true)
    const request = location
      ? updateCorporateLocation(corporateId, location.id, values)
      : createCorporateLocation(corporateId, values)
    request
      .then(() => {
        notification.success({ message: translate('corporate.location.saveSuccess') })
        onSaved()
      })
      .catch(error => {
        notification.error({
          message: translate('corporate.location.saveFailed'),
          description: error?.message,
        })
      })
      .finally(() => setLoading(false))
  }

  return (
    <Modal
      centered
      open={open}
      footer={null}
      forceRender
      width={680}
      title={translate(location ? 'corporate.location.edit' : 'corporate.location.add')}
      onCancel={onCancel}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 15 }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="cityId"
          label={translate('corporate.location.city')}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={cityOptions}
            onChange={() => form.setFieldValue('businessDistrictId', undefined)}
          />
        </Form.Item>
        <Form.Item
          name="businessDistrictId"
          label={translate('corporate.location.businessDistrict')}
          rules={[{ required: true }]}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={businessDistrictOptions}
          />
        </Form.Item>
        <Form.Item
          name="address"
          label={translate('corporate.location.address')}
          rules={[{ required: true, whitespace: true, max: 500 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="longitude"
          label={translate('corporate.location.longitude')}
          rules={[{ required: true, type: 'number', min: -180, max: 180 }]}
        >
          <InputNumber precision={7} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="latitude"
          label={translate('corporate.location.latitude')}
          rules={[{ required: true, type: 'number', min: -90, max: 90 }]}
        >
          <InputNumber precision={7} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="memberCount"
          label={translate('corporate.location.memberCount')}
          rules={[{ required: true, type: 'number', min: 0 }]}
        >
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label=" " colon={false}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {translate('submit')}
            </Button>
            <Button onClick={onCancel}>{translate('cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default CorporateLocationModal
